import { useQuery } from '@tanstack/react-query';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { Autocomplete, Stack, TextField } from '@mui/material';

import ApiClient from '@barboxx/api';
import { Business } from '@barboxx/models/types';

import { useAuth } from '../authentication';

type BusinessOption = Business.SearchResponse['results'][number];

export interface BusinessSelectorProps {
    businessId?: number;
    BusinessSelectorComponent: () => React.ReactNode | null;
    businessList: BusinessOption[];
}

export const BusinessSelectorContext = createContext<BusinessSelectorProps>({
    businessId: undefined,
    BusinessSelectorComponent: () => null,
} as BusinessSelectorProps);

export const BusinessSelectorProvider = ({
    children,
}: React.PropsWithChildren) => {
    const {
        auth: { user },
        initializing,
    } = useAuth();
    const [business, setBusiness] = useState<BusinessOption | null>(null);

    const businessesQueryResult = useQuery({
        queryKey: ['businesses'],
        queryFn: () =>
            ApiClient.Businesses.getBusinesses({ page: 0, size: 100 }),
        enabled: !!user && !initializing,
    });

    const BusinessSelectorComponent = useMemo(() => {
        const handlePlaceChange = (
            _event: React.SyntheticEvent,
            value: BusinessOption | null
        ) => {
            setBusiness(value);
        };

        if (!user) {
            return () => null;
        }

        return function BusinessSelectorComponent() {
            return (
                <Stack
                    sx={{
                        mx: 1,
                        mb: 2,
                    }}
                >
                    <Autocomplete
                        loading={businessesQueryResult.isLoading}
                        options={
                            businessesQueryResult.data?.data?.results ?? []
                        }
                        value={business}
                        fullWidth
                        isOptionEqualToValue={(
                            option: BusinessOption,
                            value
                        ) => {
                            return option?.id === value?.id;
                        }}
                        getOptionLabel={option => {
                            return option?.place?.name ?? option?.id.toString();
                        }}
                        onChange={handlePlaceChange}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Filter on Business"
                                placeholder={
                                    user?.userType === 'Internal'
                                        ? 'All Businesses'
                                        : 'Your Business'
                                }
                            />
                        )}
                    />
                </Stack>
            );
        };
    }, [
        business,
        businessesQueryResult.data?.data?.results,
        businessesQueryResult.isLoading,
        user,
    ]);

    return (
        <BusinessSelectorContext.Provider
            value={{
                businessId: business?.id,
                BusinessSelectorComponent,
                businessList: businessesQueryResult.data?.data?.results ?? [],
            }}
        >
            {children}
        </BusinessSelectorContext.Provider>
    );
};

export const useBusinessSelector = () => useContext(BusinessSelectorContext);
